'use client';

import { useRef, type MouseEvent, type PointerEvent } from 'react';
import { Box } from '@mui/material';
import { EASE_SHEET } from './motion';
import { haptic } from './haptics';

const SHEET_GAP = 10;
const DISMISS_FRACTION = 0.25;
const FLING = 0.6;
const SETTLE_MS = 320;

export const SHEET_TOP = `calc(env(safe-area-inset-top) + ${SHEET_GAP}px)`;

export const sheetPaper = {
  mt: SHEET_TOP,
  height: `calc(100% - ${SHEET_TOP})`,
  maxHeight: 'none',
  borderTopLeftRadius: '14px',
  borderTopRightRadius: '14px',
  overflow: 'hidden',
} as const;

export function SheetGrabber() {
  return (
    <Box
      aria-hidden
      sx={{ position: 'absolute', top: 6, left: '50%', width: 36, height: 5, ml: '-18px', borderRadius: 999, bgcolor: 'color-mix(in srgb, var(--ink) 18%, transparent)', pointerEvents: 'none' }}
    />
  );
}

type Gesture = { id: number; y: number; dy: number; lastY: number; lastT: number; velocity: number; engaged: boolean; paper: HTMLElement; backdrop: HTMLElement | null };

type Options = { onDismiss: () => void; canDismiss?: () => boolean; enabled?: boolean };

export function useSheetDrag({ onDismiss, canDismiss, enabled = true }: Options) {
  const gesture = useRef<Gesture | null>(null);
  const suppressClick = useRef(false);

  const settle = ({ paper, backdrop }: Gesture) => {
    paper.style.transition = `transform ${SETTLE_MS}ms ${EASE_SHEET}`;
    paper.style.transform = 'none';
    if (backdrop) {
      backdrop.style.transition = `opacity ${SETTLE_MS}ms ease`;
      backdrop.style.opacity = '1';
    }
    window.setTimeout(() => {
      paper.style.transition = '';
      if (backdrop) backdrop.style.transition = '';
    }, SETTLE_MS);
  };

  const onPointerDown = (event: PointerEvent<HTMLElement>) => {
    if (!enabled || event.pointerType === 'mouse' || !event.isPrimary) return;
    const paper = event.currentTarget.closest<HTMLElement>('.MuiDialog-paper, [data-sheet-paper]');
    if (!paper) return;
    const backdrop = paper.closest('.MuiDialog-root, [data-sheet-root]')?.querySelector<HTMLElement>('.MuiBackdrop-root, [data-sheet-backdrop]') ?? null;
    gesture.current = { id: event.pointerId, y: event.clientY, dy: 0, lastY: event.clientY, lastT: event.timeStamp, velocity: 0, engaged: false, paper, backdrop };
  };

  const onPointerMove = (event: PointerEvent<HTMLElement>) => {
    const g = gesture.current;
    if (!g || g.id !== event.pointerId) return;
    const dy = event.clientY - g.y;
    if (!g.engaged) {
      if (Math.abs(dy) < 6) return;
      if (dy < 0) {
        gesture.current = null;
        return;
      }
      g.engaged = true;
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    const elapsed = event.timeStamp - g.lastT;
    if (elapsed > 0) g.velocity = 0.7 * ((event.clientY - g.lastY) / elapsed) + 0.3 * g.velocity;
    g.lastY = event.clientY;
    g.lastT = event.timeStamp;
    g.dy = dy >= 0 ? dy : -Math.sqrt(-dy) * 2;
    g.paper.style.transition = 'none';
    g.paper.style.transform = `translate3d(0, ${g.dy}px, 0)`;
    if (g.backdrop) {
      g.backdrop.style.transition = 'none';
      g.backdrop.style.opacity = String(Math.max(0, 1 - Math.max(0, g.dy) / g.paper.offsetHeight));
    }
  };

  const finish = (cancelled: boolean) => {
    const g = gesture.current;
    gesture.current = null;
    if (!g?.engaged) return;
    suppressClick.current = true;
    const far = g.dy > g.paper.offsetHeight * DISMISS_FRACTION || (g.velocity > FLING && g.dy > 24);
    if (!cancelled && far && (!canDismiss || canDismiss())) {
      haptic();
      onDismiss();
      return;
    }
    settle(g);
  };

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp: () => finish(false),
    onPointerCancel: () => finish(true),
    onClickCapture: (event: MouseEvent) => {
      if (!suppressClick.current) return;
      suppressClick.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
  };
}
